import { useState } from "react";

/**
 * Put-away rows go to the bottom, behind a disclosure (#486).
 *
 * ── THE PROBLEM ──
 *
 * Asked 5 Oct: a Manage box on an account that has tidied a few lists
 * over a couple of seasons is mostly rows it has deliberately stopped
 * using. Confirmed — every manager rendered put-away items **inline and
 * interleaved** with active ones, in the same list, with nothing sorting
 * them down and nothing collapsing them.
 *
 * ── WHY THIS RATHER THAN THE SIX-MONTH PROMPT ──
 *
 * The original proposal was a periodic "this has been hidden six months,
 * do you want it gone for good?". #486 records the four reasons against
 * it; the two that decided it:
 *
 *   - `is_active` is a bare BIT on every one of these tables. There is no
 *     `retired_at` anywhere in the schema, so "put away for six months"
 *     is not a question the database can answer about anything yet.
 *   - "Permanently hidden" hides things from the one screen whose job is
 *     showing you what you put away. Put-away items are already excluded
 *     from every picker — that is what putting one away does.
 *
 * This needs no schema change, no scheduled job, no third state and no
 * prompt, and it solves the stated problem completely: the Manage box
 * opens showing what you use.
 *
 * ── THE SHAPE IS BORROWED, NOT INVENTED ──
 *
 * `components/journal/CollapsibleChips` already does this for many tags,
 * most of them not currently interesting. Same problem, so the same
 * vocabulary — "Show N put away" — rather than a second idiom.
 *
 * ── WHY A SPLIT HELPER AND NOT A SHARED ROW RENDERER ──
 *
 * Four managers show genuinely different rows: a varietal carries a
 * colour and a default flag, a jar size carries pounds, an option list
 * row carries an inline editor. Forcing one row component would mean a
 * props bag with a branch per caller, which is how a shared component
 * becomes four components in a trench coat.
 *
 * So what is shared is the RULE and the WORDS. Each manager keeps its own
 * row markup.
 */

/**
 * Split a list into what is in use and what has been put away.
 *
 * `isActive` is passed in because the flag is not spelled the same way
 * everywhere: `OptionListManager` reads a truthy `o.is_active`, while
 * `BottleSizeManager` tests `r.is_active === false` because its list
 * endpoint historically omitted the column entirely and an absent flag
 * there means active. Hard-coding either reading would silently put the
 * whole list in the wrong half on one screen.
 *
 * ORDER IS PRESERVED inside each half. The caller has usually sorted
 * already — alphabetically, or defaults first — and re-sorting here would
 * quietly override that.
 */
export function splitPutAway(items = [], isActive = (item) => item.is_active !== false) {
  const active = [];
  const putAway = [];
  for (const item of items) {
    (isActive(item) ? active : putAway).push(item);
  }
  return { active, putAway };
}

/**
 * The disclosure itself.
 *
 * RENDERS NOTHING WHEN THERE IS NOTHING PUT AWAY, which is the point: an
 * account that has never put anything away sees no extra control at all,
 * so the common case gets simpler rather than gaining a widget. A
 * disclosure reading "Show 0 put away" would be worse than the clutter
 * it replaced.
 */
export function PutAwayDisclosure({ count, open, onToggle, noun = "put away" }) {
  if (!count) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="link-button"
    >
      {open ? `Hide ${count} ${noun}` : `Show ${count} ${noun}`}
    </button>
  );
}

/**
 * The whole pattern, for callers that just want it.
 *
 * `children` is called with the two halves and the open state, so the
 * caller still writes its own rows:
 *
 *     <PutAwaySection items={rows} isActive={(r) => r.is_active !== false}>
 *       {({ active, putAway, open, disclosure }) => (
 *         <ul>
 *           {active.map(renderRow)}
 *           {disclosure}
 *           {open && putAway.map(renderRow)}
 *         </ul>
 *       )}
 *     </PutAwaySection>
 *
 * The disclosure is handed back as an element rather than left to the
 * caller to place, so the words and the open state cannot drift between
 * the four managers.
 */
export function PutAwaySection({ items, isActive, noun, children }) {
  const [open, setOpen] = useState(false);
  const { active, putAway } = splitPutAway(items, isActive);

  return children({
    active,
    putAway,
    open,
    disclosure: (
      <PutAwayDisclosure
        count={putAway.length}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        noun={noun}
      />
    ),
  });
}
